import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, useCallback, useContext, useMemo, type ReactNode } from "react";
import { api, isUnauthorized } from "../api/client";
import { keys } from "../api/queries";
import type { Admin } from "../api/schemas";
import { can, type Permission } from "./permissions";

interface AuthValue {
  admin: Admin | null;
  status: "loading" | "authenticated" | "anonymous" | "error";
  error: unknown;
  login: (email: string, password: string) => Promise<Admin>;
  logout: () => Promise<void>;
  refetch: () => void;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const me = useQuery({
    queryKey: keys.me,
    queryFn: async ({ signal }) => {
      try {
        return (await api.me(signal)).admin;
      } catch (e) {
        if (isUnauthorized(e)) return null;
        throw e;
      }
    },
    staleTime: 5 * 60_000,
    retry: 1,
  });

  const login = useCallback(
    async (email: string, password: string) => {
      const { admin } = await api.login(email, password);
      qc.setQueryData(keys.me, admin);
      return admin;
    },
    [qc],
  );

  const logout = useCallback(async () => {
    try {
      await api.logout();
    } finally {
      qc.clear();
      qc.setQueryData(keys.me, null);
    }
  }, [qc]);

  const value = useMemo<AuthValue>(
    () => ({
      admin: me.data ?? null,
      status: me.isPending ? "loading" : me.isError ? "error" : me.data ? "authenticated" : "anonymous",
      error: me.error,
      login,
      logout,
      refetch: () => void me.refetch(),
    }),
    [me, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}

export function useCan(permission: Permission): boolean {
  return can(useAuth().admin?.role, permission);
}
